(() => {
  'use strict';

  const ANNOUNCEMENT_SELECTOR = '[data-premium-announcement]';
  const ITEM_SELECTOR = '[data-announcement-item]';

  /**
   * Initialise one premium announcement bar.
   *
   * Supports:
   * - Static announcements
   * - Rotating announcements
   * - Previous/next buttons
   * - Theme Editor section reloads
   * - Theme Editor block selection
   */
  function initialiseAnnouncementBar(bar) {
    if (!bar || bar.dataset.premiumAnnouncementReady === 'true') {
      return;
    }

    const items = Array.from(bar.querySelectorAll(ITEM_SELECTOR));

    if (!items.length) {
      return;
    }

    bar.dataset.premiumAnnouncementReady = 'true';

    let currentIndex = 0;
    let rotationTimer = null;

    const speedValue = window
      .getComputedStyle(bar)
      .getPropertyValue('--pa-speed')
      .trim();

    const parsedSpeed = Number.parseFloat(speedValue);

    const rotationSpeed = Number.isFinite(parsedSpeed)
      ? Math.max(3, parsedSpeed) * 1000
      : 5000;

    /**
     * Display one announcement and hide the others.
     */
    function showItem(index) {
      currentIndex = (index + items.length) % items.length;

      items.forEach((item, itemIndex) => {
        const isActive = itemIndex === currentIndex;

        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    /**
     * Stop the automatic rotation.
     */
    function stopRotation() {
      if (!rotationTimer) {
        return;
      }

      window.clearInterval(rotationTimer);
      rotationTimer = null;
    }

    /**
     * Start the automatic rotation.
     */
    function startRotation() {
      stopRotation();

      if (
        !bar.classList.contains('premium-announcement--rotate') ||
        items.length < 2
      ) {
        return;
      }

      rotationTimer = window.setInterval(() => {
        showItem(currentIndex + 1);
      }, rotationSpeed);
    }

    const nextButton = bar.querySelector('[data-announcement-next]');
    const previousButton = bar.querySelector('[data-announcement-prev]');

    if (bar.classList.contains('premium-announcement--rotate')) {
      showItem(0);

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          showItem(currentIndex + 1);
          startRotation();
        });
      }

      if (previousButton) {
        previousButton.addEventListener('click', () => {
          showItem(currentIndex - 1);
          startRotation();
        });
      }

      /*
       * Pause while the customer is interacting with the bar.
       */
      bar.addEventListener('mouseenter', stopRotation);
      bar.addEventListener('mouseleave', startRotation);
      bar.addEventListener('focusin', stopRotation);
      bar.addEventListener('focusout', startRotation);

      startRotation();
    } else {
      /*
       * Static and marquee layouts should display every item.
       */
      items.forEach((item) => {
        item.classList.add('is-active');
        item.setAttribute('aria-hidden', 'false');
      });
    }

    /**
     * Cleanup method used when Shopify unloads the section
     * inside the Theme Editor.
     */
    bar.premiumAnnouncementDestroy = () => {
      stopRotation();
      delete bar.dataset.premiumAnnouncementReady;
    };
  }

  /**
   * Find and initialise announcement bars within a given element.
   */
  function initialiseAll(root = document) {
    if (
      root.matches &&
      root.matches(ANNOUNCEMENT_SELECTOR)
    ) {
      initialiseAnnouncementBar(root);
    }

    if (!root.querySelectorAll) {
      return;
    }

    root
      .querySelectorAll(ANNOUNCEMENT_SELECTOR)
      .forEach(initialiseAnnouncementBar);
  }

  /**
   * Initial page load.
   */
  function start() {
    initialiseAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /**
   * Reinitialise when Shopify reloads the header
   * in the Theme Customiser.
   */
  document.addEventListener('shopify:section:load', (event) => {
    initialiseAll(event.target);
  });

  /**
   * Clean up timers when Shopify unloads the section.
   */
  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target || !event.target.querySelectorAll) {
      return;
    }

    event.target
      .querySelectorAll(ANNOUNCEMENT_SELECTOR)
      .forEach((bar) => {
        if (typeof bar.premiumAnnouncementDestroy === 'function') {
          bar.premiumAnnouncementDestroy();
        }
      });
  });

  /**
   * When an announcement block is selected in the Theme
   * Customiser, display that specific block.
   */
  document.addEventListener('shopify:block:select', (event) => {
    if (!event.target || !event.target.closest) {
      return;
    }

    const selectedItem = event.target.closest(ITEM_SELECTOR);

    if (!selectedItem) {
      return;
    }

    const bar = selectedItem.closest(ANNOUNCEMENT_SELECTOR);

    if (
      !bar ||
      !bar.classList.contains('premium-announcement--rotate')
    ) {
      return;
    }

    const items = Array.from(bar.querySelectorAll(ITEM_SELECTOR));

    items.forEach((item) => {
      const isSelected = item === selectedItem;

      item.classList.toggle('is-active', isSelected);
      item.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
    });
  });
})();